import React, { useState, useEffect } from 'react';
import Layout from '../components/common/Layout';
import { consumoService } from '../services/consumo';
import toast from 'react-hot-toast';
import {
    Zap,
    Droplets,
    Flame,
    Fuel,
    MoreHorizontal,
    Upload,
    Download,
    Trash2,
    ChevronDown,
    ChevronUp,
    FileText,
    BarChart3,
    X,
    TrendingUp,
    TrendingDown,
    Minus,
    Plus,
    Building2,
    Sparkles,
    AlertCircle,
} from 'lucide-react';

// ─── Constantes ───────────────────────────────────────────────────────────────

const ALMACENES = [
    'CD Qolqas', 'Ica', 'Piura', 'Chimbote',
    'Arequipa', 'Trujillo', 'Huancayo', 'Chiclayo',
];
const MESES = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

// Configuración por tipo de servicio para consumo manual por almacén.
const CONSUMO_CONFIG = {
    energia:     { unidad: 'kWh', tarifa: 0.62, label: 'kWh',   icon: 'Zap',            color: 'yellow' },
    agua:        { unidad: 'm³',  tarifa: 5.50, label: 'm³',    icon: 'Droplets',       color: 'blue'   },
    gas:         { unidad: 'Sm³', tarifa: 1.50, label: 'Sm³',   icon: 'Flame',          color: 'orange' },
    combustible: { unidad: 'gal', tarifa: 15.0, label: 'gal',   icon: 'Fuel',           color: 'red'    },
    residuos:    { unidad: 'kg',  tarifa: 0,    label: 'kg',    icon: 'Trash2',         color: 'green'  },
    otro:        { unidad: 'u',   tarifa: 1.00, label: 'unid.', icon: 'MoreHorizontal', color: 'gray'   },
};

// ─── Modal Subir PDF ──────────────────────────────────────────────────────────

const EXTENSIONES_ACEPTADAS = '.pdf,.xlsx,.xls,.csv';

const ModalSubirPDF = ({ tipo, onCerrar, onSubido }) => {
    const [almacen, setAlmacen] = useState('');
    const [mes, setMes] = useState(MESES[new Date().getMonth()]);
    const [año, setAño] = useState(String(new Date().getFullYear()));
    const [archivo, setArchivo] = useState(null);
    const [subiendo, setSubiendo] = useState(false);

    const handleSubir = async () => {
        if (!almacen) { toast.error('Selecciona un almacén'); return; }
        if (!archivo) { toast.error('Selecciona un archivo (PDF, Excel o CSV)'); return; }
        const periodo = `${mes} ${año}`;
        setSubiendo(true);
        try {
            await consumoService.subirPDF(tipo, archivo, almacen, periodo);
            toast.success('Archivo subido correctamente');
            onSubido();
            onCerrar();
        } catch (err) {
            toast.error(err?.response?.data?.detail || 'Error al subir el archivo');
        } finally {
            setSubiendo(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-md">
                <div className="flex items-center justify-between px-6 py-4 border-b">
                    <div className="flex items-center space-x-2">
                        <Upload className="h-5 w-5 text-blue-600" />
                        <h2 className="text-lg font-bold text-gray-900">Subir Recibo (PDF / Excel / CSV)</h2>
                    </div>
                    <button onClick={onCerrar}><X className="h-5 w-5 text-gray-400 hover:text-gray-600" /></button>
                </div>
                <div className="px-6 py-5 space-y-4">
                    {/* Almacén */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Almacén</label>
                        <select
                            value={almacen}
                            onChange={(e) => setAlmacen(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                        >
                            <option value="">Seleccionar almacén...</option>
                            {ALMACENES.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>
                    {/* Mes y Año */}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Mes</label>
                            <select
                                value={mes}
                                onChange={(e) => setMes(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            >
                                {MESES.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Año</label>
                            <input
                                type="number"
                                value={año}
                                onChange={(e) => setAño(e.target.value)}
                                min="2020" max="2099"
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                            />
                        </div>
                    </div>
                    {/* Archivo */}
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Archivo (PDF, Excel o CSV)
                        </label>
                        <input
                            type="file"
                            accept={EXTENSIONES_ACEPTADAS}
                            onChange={(e) => setArchivo(e.target.files[0] || null)}
                            className="w-full text-sm text-gray-500 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                        />
                        {archivo && <p className="text-xs text-gray-400 mt-1">{archivo.name}</p>}
                        <p className="text-xs text-gray-400 mt-1">Formatos: PDF, XLSX, XLS, CSV — máx. 10 MB</p>
                    </div>
                </div>
                <div className="flex justify-end space-x-3 px-6 pb-5">
                    <button onClick={onCerrar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                        Cancelar
                    </button>
                    <button
                        onClick={handleSubir}
                        disabled={subiendo || !archivo || !almacen}
                        className="px-5 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 disabled:opacity-60 flex items-center"
                    >
                        {subiendo
                            ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            : <Upload className="h-4 w-4 mr-1.5" />
                        }
                        Subir archivo
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Modal KWH ────────────────────────────────────────────────────────────────

const ICONS_BY_NAME = { Zap, Droplets, Flame, Fuel, Trash2, MoreHorizontal };

// Lookup estático de clases Tailwind por color (necesario para que JIT las compile).
const MODAL_COLORS = {
    yellow: {
        iconText: 'text-yellow-500',
        ring: 'focus:ring-yellow-400',
        totalBg: 'bg-yellow-50',
        totalBorder: 'border-yellow-200',
        totalText: 'text-yellow-700',
        btn: 'bg-yellow-500 hover:bg-yellow-600',
    },
    blue: {
        iconText: 'text-blue-500',
        ring: 'focus:ring-blue-400',
        totalBg: 'bg-blue-50',
        totalBorder: 'border-blue-200',
        totalText: 'text-blue-700',
        btn: 'bg-blue-500 hover:bg-blue-600',
    },
    orange: {
        iconText: 'text-orange-500',
        ring: 'focus:ring-orange-400',
        totalBg: 'bg-orange-50',
        totalBorder: 'border-orange-200',
        totalText: 'text-orange-700',
        btn: 'bg-orange-500 hover:bg-orange-600',
    },
    gray: {
        iconText: 'text-gray-500',
        ring: 'focus:ring-gray-400',
        totalBg: 'bg-gray-50',
        totalBorder: 'border-gray-300',
        totalText: 'text-gray-700',
        btn: 'bg-gray-600 hover:bg-gray-700',
    },
    red: {
        iconText: 'text-red-500',
        ring: 'focus:ring-red-400',
        totalBg: 'bg-red-50',
        totalBorder: 'border-red-200',
        totalText: 'text-red-700',
        btn: 'bg-red-500 hover:bg-red-600',
    },
    green: {
        iconText: 'text-green-500',
        ring: 'focus:ring-green-400',
        totalBg: 'bg-green-50',
        totalBorder: 'border-green-200',
        totalText: 'text-green-700',
        btn: 'bg-green-500 hover:bg-green-600',
    },
};

const ModalKWH = ({ tipo = 'energia', onCerrar, onGuardado }) => {
    const cfg = CONSUMO_CONFIG[tipo] || CONSUMO_CONFIG.energia;
    const Icon = ICONS_BY_NAME[cfg.icon] || Zap;
    const mc = MODAL_COLORS[cfg.color] || MODAL_COLORS.yellow;

    const [mes, setMes] = useState(MESES[new Date().getMonth()]);
    const [año, setAño] = useState(String(new Date().getFullYear()));
    const [cantidades, setCantidades] = useState(
        Object.fromEntries(ALMACENES.map((a) => [a, '']))
    );
    const [tarifas, setTarifas] = useState(
        Object.fromEntries(ALMACENES.map((a) => [a, String(cfg.tarifa)]))
    );
    const [reciclados, setReciclados] = useState(
        Object.fromEntries(ALMACENES.map((a) => [a, false]))
    );
    const [guardando, setGuardando] = useState(false);

    const calcularSoles = (cantidad, tarifa) => {
        const k = parseFloat(cantidad);
        const t = parseFloat(tarifa);
        return isNaN(k) || k <= 0 || isNaN(t) || t <= 0 ? '-' : `S/. ${(k * t).toFixed(2)}`;
    };

    const totalCantidad = Object.values(cantidades).reduce((s, v) => s + (parseFloat(v) || 0), 0);
    const totalSoles = ALMACENES.reduce((s, a) => {
        const k = parseFloat(cantidades[a]) || 0;
        const t = parseFloat(tarifas[a]) || 0;
        return s + k * t;
    }, 0);

    const handleGuardar = async () => {
        const entradas = ALMACENES
            .filter((a) => parseFloat(cantidades[a]) > 0)
            .map((a) => ({
                almacen: a,
                kwh: parseFloat(cantidades[a]),
                tarifa_kwh: parseFloat(tarifas[a]) || cfg.tarifa,
                ...(tipo === 'residuos' ? { es_reciclado: !!reciclados[a] } : {}),
            }));
        if (entradas.length === 0) { toast.error(`Ingresa el ${cfg.unidad} de al menos un almacén`); return; }
        if (!mes || !año) { toast.error('El mes y año son obligatorios'); return; }
        setGuardando(true);
        const periodo = `${mes} ${año}`;
        try {
            await consumoService.guardarKWH(periodo, entradas, tipo);
            toast.success(`${entradas.length} almacén(es) guardados`);
            onGuardado();
            onCerrar();
        } catch { toast.error('Error al guardar'); }
        finally { setGuardando(false); }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between px-6 py-5 border-b sticky top-0 bg-white z-10">
                    <div className="flex items-center space-x-2">
                        <Icon className={`h-5 w-5 ${mc.iconText}`} />
                        <h2 className="text-lg font-bold text-gray-900">
                            Agregar {cfg.unidad} por Almacén
                        </h2>
                    </div>
                    <button onClick={onCerrar} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
                </div>

                <div className="px-6 py-5 space-y-5">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Mes</label>
                            <select
                                value={mes}
                                onChange={(e) => setMes(e.target.value)}
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 ${mc.ring}`}
                            >
                                {MESES.map((m) => <option key={m} value={m}>{m}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-gray-700 mb-1">Año</label>
                            <input
                                type="number"
                                value={año}
                                onChange={(e) => setAño(e.target.value)}
                                min="2020" max="2099"
                                className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 ${mc.ring}`}
                            />
                        </div>
                    </div>

                    <div className="border border-gray-200 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-gray-50 border-b border-gray-200">
                                    <th className="text-left px-4 py-3 font-semibold text-gray-600">Almacén</th>
                                    <th className="text-center px-4 py-3 font-semibold text-gray-600">{cfg.unidad}</th>
                                    <th className="text-center px-4 py-3 font-semibold text-gray-600">Tarifa (S/./{cfg.unidad})</th>
                                    <th className="text-right px-4 py-3 font-semibold text-gray-600">Soles</th>
                                    {tipo === 'residuos' && (
                                        <th className="text-center px-4 py-3 font-semibold text-gray-600">Reciclado</th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {ALMACENES.map((almacen, i) => (
                                    <tr key={almacen} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
                                        <td className="px-4 py-3">
                                            <div className="flex items-center space-x-2">
                                                <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                                                <span className="font-medium text-gray-800">{almacen}</span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <input
                                                type="number" min="0" step="0.01"
                                                value={cantidades[almacen]}
                                                onChange={(e) => setCantidades((prev) => ({ ...prev, [almacen]: e.target.value }))}
                                                placeholder="0.00"
                                                className={`w-24 px-2 py-1.5 border border-gray-200 rounded-lg text-center text-sm focus:outline-none focus:ring-2 ${mc.ring}`}
                                            />
                                        </td>
                                        <td className="px-4 py-2 text-center">
                                            <input
                                                type="number" min="0" step="0.001"
                                                value={tarifas[almacen]}
                                                onChange={(e) => setTarifas((prev) => ({ ...prev, [almacen]: e.target.value }))}
                                                placeholder={cfg.tarifa.toFixed(3)}
                                                className="w-24 px-2 py-1.5 border border-blue-200 rounded-lg text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-blue-50"
                                            />
                                        </td>
                                        <td className="px-4 py-3 text-right font-semibold text-green-700">
                                            {calcularSoles(cantidades[almacen], tarifas[almacen])}
                                        </td>
                                        {tipo === 'residuos' && (
                                            <td className="px-4 py-2 text-center">
                                                <input
                                                    type="checkbox"
                                                    checked={reciclados[almacen]}
                                                    onChange={(e) => setReciclados((prev) => ({ ...prev, [almacen]: e.target.checked }))}
                                                    className="h-4 w-4"
                                                    title="Este residuo fue reciclado/revalorizado (no fue a relleno sanitario)"
                                                />
                                            </td>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                            {totalCantidad > 0 && (
                                <tfoot>
                                    <tr className={`${mc.totalBg} border-t-2 ${mc.totalBorder}`}>
                                        <td className="px-4 py-3 font-bold text-gray-800">TOTAL</td>
                                        <td className={`px-4 py-3 text-center font-bold ${mc.totalText}`}>
                                            {totalCantidad.toLocaleString('es-PE', { maximumFractionDigits: 2 })} {cfg.unidad}
                                        </td>
                                        <td></td>
                                        <td className="px-4 py-3 text-right font-bold text-green-700">
                                            S/. {totalSoles.toFixed(2)}
                                        </td>
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>
                </div>

                <div className="flex justify-end space-x-3 px-6 pb-6">
                    <button onClick={onCerrar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">Cancelar</button>
                    <button
                        onClick={handleGuardar}
                        disabled={guardando || totalCantidad === 0}
                        className={`px-5 py-2 ${mc.btn} text-white rounded-lg text-sm font-semibold disabled:opacity-60 transition-colors flex items-center`}
                    >
                        {guardando
                            ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            : <Plus className="h-4 w-4 mr-1.5" />
                        }
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Lista KWH guardados ──────────────────────────────────────────────────────

// Clases estáticas por tipo para el accordion (para que Tailwind las incluya).
const LISTA_COLORS = {
    yellow: { headerBg: 'bg-yellow-50 hover:bg-yellow-100', title: 'text-yellow-800', subText: 'text-yellow-600', icon: 'text-yellow-500' },
    blue:   { headerBg: 'bg-blue-50 hover:bg-blue-100',     title: 'text-blue-800',   subText: 'text-blue-600',   icon: 'text-blue-500' },
    orange: { headerBg: 'bg-orange-50 hover:bg-orange-100', title: 'text-orange-800', subText: 'text-orange-600', icon: 'text-orange-500' },
    gray:   { headerBg: 'bg-gray-50 hover:bg-gray-100',     title: 'text-gray-800',   subText: 'text-gray-600',   icon: 'text-gray-500' },
};

const ListaKWH = ({ registros, onEliminar, tipo = 'energia' }) => {
    const cfg = CONSUMO_CONFIG[tipo] || CONSUMO_CONFIG.energia;
    const lc = LISTA_COLORS[cfg.color] || LISTA_COLORS.yellow;
    const porPeriodo = registros.reduce((acc, r) => {
        if (!acc[r.periodo]) acc[r.periodo] = [];
        acc[r.periodo].push(r);
        return acc;
    }, {});
    const [abiertos, setAbiertos] = useState({});
    if (registros.length === 0) return null;

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mt-6">
            <div className="flex items-center mb-4">
                <Building2 className={`h-5 w-5 mr-2 ${lc.icon}`} />
                <h2 className="text-lg font-semibold text-gray-800">{cfg.unidad} por Almacén</h2>
            </div>
            {Object.entries(porPeriodo).map(([periodo, items]) => {
                const totalCantidad = items.reduce((s, r) => s + r.kwh, 0);
                const totalSoles = items.reduce((s, r) => s + r.soles, 0);
                const isOpen = abiertos[periodo] ?? true;
                return (
                    <div key={periodo} className="border border-gray-200 rounded-xl overflow-hidden mb-3">
                        <button
                            onClick={() => setAbiertos((p) => ({ ...p, [periodo]: !isOpen }))}
                            className={`w-full flex items-center justify-between px-5 py-3 ${lc.headerBg} transition-colors`}
                        >
                            <div className="flex items-center space-x-3">
                                <span className={`font-semibold ${lc.title}`}>{periodo}</span>
                                <span className={`text-sm ${lc.subText}`}>
                                    {totalCantidad.toLocaleString('es-PE', { maximumFractionDigits: 2 })} {cfg.unidad}
                                </span>
                                <span className="text-sm font-semibold text-green-700">S/. {totalSoles.toFixed(2)}</span>
                            </div>
                            {isOpen ? <ChevronUp className={`h-4 w-4 ${lc.subText}`} /> : <ChevronDown className={`h-4 w-4 ${lc.subText}`} />}
                        </button>
                        {isOpen && (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="border-b border-gray-100 bg-gray-50">
                                            <th className="text-left px-5 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Almacén</th>
                                            <th className="text-center px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">{cfg.unidad}</th>
                                            <th className="text-center px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tarifa</th>
                                            <th className="text-right px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">Soles</th>
                                            <th className="px-4 py-2"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.map((r) => (
                                            <tr key={r.id} className="border-b border-gray-50 hover:bg-gray-50">
                                                <td className="px-5 py-2.5 font-medium text-gray-800">{r.almacen}</td>
                                                <td className="px-4 py-2.5 text-center text-gray-700">
                                                    {r.kwh.toLocaleString('es-PE', { maximumFractionDigits: 2 })}
                                                </td>
                                                <td className="px-4 py-2.5 text-center text-blue-600 text-xs font-medium">
                                                    S/. {r.tarifa_kwh?.toFixed(3) ?? '-'}
                                                </td>
                                                <td className="px-4 py-2.5 text-right font-semibold text-green-700">
                                                    S/. {r.soles.toFixed(2)}
                                                </td>
                                                <td className="px-4 py-2.5 text-right">
                                                    <button onClick={() => onEliminar(r.id)} className="text-red-400 hover:text-red-600 transition-colors">
                                                        <Trash2 className="h-4 w-4" />
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

// ─── Configuración de tabs ────────────────────────────────────────────────────

const TABS = [
    { key: 'energia', label: 'Energía', icon: Zap, color: 'yellow', unidad: 'kWh' },
    { key: 'agua', label: 'Agua', icon: Droplets, color: 'blue', unidad: 'm³' },
    { key: 'gas', label: 'Gas', icon: Flame, color: 'orange', unidad: 'm³' },
    { key: 'combustible', label: 'Combustible', icon: Fuel, color: 'red', unidad: 'gal' },
    { key: 'residuos', label: 'Residuos', icon: Trash2, color: 'green', unidad: 'kg' },
    { key: 'otro', label: 'Otros', icon: MoreHorizontal, color: 'gray', unidad: '-' },
];

const TAB_COLORS = {
    yellow: {
        active: 'bg-yellow-500 text-white border-yellow-500',
        inactive: 'bg-white text-yellow-600 border-yellow-300 hover:bg-yellow-50',
        badge: 'bg-yellow-100 text-yellow-700',
        icon: 'text-yellow-500',
    },
    blue: {
        active: 'bg-blue-500 text-white border-blue-500',
        inactive: 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50',
        badge: 'bg-blue-100 text-blue-700',
        icon: 'text-blue-500',
    },
    orange: {
        active: 'bg-orange-500 text-white border-orange-500',
        inactive: 'bg-white text-orange-600 border-orange-300 hover:bg-orange-50',
        badge: 'bg-orange-100 text-orange-700',
        icon: 'text-orange-500',
    },
    gray: {
        active: 'bg-gray-600 text-white border-gray-600',
        inactive: 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50',
        badge: 'bg-gray-100 text-gray-700',
        icon: 'text-gray-500',
    },
    red: {
        active: 'bg-red-500 text-white border-red-500',
        inactive: 'bg-white text-red-600 border-red-300 hover:bg-red-50',
        badge: 'bg-red-100 text-red-700',
        icon: 'text-red-500',
    },
    green: {
        active: 'bg-green-500 text-white border-green-500',
        inactive: 'bg-white text-green-600 border-green-300 hover:bg-green-50',
        badge: 'bg-green-100 text-green-700',
        icon: 'text-green-500',
    },
};

// ─── Componente Accordion por registro ────────────────────────────────────────

const ConsumoAccordion = ({ registro, onEliminar, onDescargar, tabColor }) => {
    const [abierto, setAbierto] = useState(false);
    const colors = TAB_COLORS[tabColor];

    const variacion = registro._variacion_pct;

    return (
        <div className="border border-gray-200 rounded-lg overflow-hidden mb-3">
            {/* Cabecera del accordion */}
            <button
                onClick={() => setAbierto(!abierto)}
                className="w-full flex items-center justify-between px-5 py-4 bg-white hover:bg-gray-50 transition-colors"
            >
                <div className="flex items-center space-x-3 flex-wrap gap-y-1">
                    {registro.almacen && (
                        <div className="flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-semibold bg-gray-100 text-gray-700">
                            <Building2 className="h-3.5 w-3.5" />
                            <span>{registro.almacen}</span>
                        </div>
                    )}
                    <div className={`px-3 py-1 rounded-full text-sm font-semibold ${colors.badge}`}>
                        {registro.periodo}
                    </div>
                    {registro.consumo_cantidad && (
                        <span className="text-gray-700 font-medium">
                            {registro.consumo_cantidad.toLocaleString('es-PE')} {registro.unidad}
                        </span>
                    )}
                    {registro.monto_total && (
                        <span className="text-gray-700 font-medium">
                            S/. {Number(registro.monto_total).toFixed(2)}
                        </span>
                    )}
                    {variacion !== undefined && variacion !== null && (
                        <span className={`flex items-center text-sm font-medium ${
                            variacion > 0 ? 'text-red-500' : variacion < 0 ? 'text-green-500' : 'text-gray-400'
                        }`}>
                            {variacion > 0 ? <TrendingUp className="h-4 w-4 mr-1" /> :
                             variacion < 0 ? <TrendingDown className="h-4 w-4 mr-1" /> :
                             <Minus className="h-4 w-4 mr-1" />}
                            {variacion > 0 ? '+' : ''}{variacion}%
                        </span>
                    )}
                </div>
                <div className="flex items-center space-x-2">
                    {registro.extraido_automaticamente ? (
                        <span className="flex items-center text-xs font-semibold text-green-700 bg-green-50 px-2 py-1 rounded-full">
                            <Sparkles className="h-3 w-3 mr-1" /> Auto-extraído
                        </span>
                    ) : registro.archivo_nombre?.toLowerCase().endsWith('.pdf') && !registro.monto_total && !registro.consumo_cantidad ? (
                        <span className="flex items-center text-xs font-semibold text-amber-700 bg-amber-50 px-2 py-1 rounded-full">
                            <AlertCircle className="h-3 w-3 mr-1" /> Completar datos
                        </span>
                    ) : null}
                    {registro.archivo_nombre && (
                        <span className="flex items-center text-xs text-gray-400 uppercase">
                            <FileText className="h-3 w-3 mr-1" />
                            {(registro.archivo_nombre.split('.').pop() || 'file').slice(0, 5)}
                        </span>
                    )}
                    {abierto ? <ChevronUp className="h-5 w-5 text-gray-400" /> : <ChevronDown className="h-5 w-5 text-gray-400" />}
                </div>
            </button>

            {/* Contenido expandido */}
            {abierto && (
                <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        {registro.almacen && (
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Almacén</p>
                                <p className="text-sm font-medium text-gray-800">{registro.almacen}</p>
                            </div>
                        )}
                        {registro.proveedor && (
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Proveedor</p>
                                <p className="text-sm font-medium text-gray-800">{registro.proveedor}</p>
                            </div>
                        )}
                        {registro.numero_cuenta && (
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">N° Cuenta / Suministro</p>
                                <p className="text-sm font-medium text-gray-800">{registro.numero_cuenta}</p>
                            </div>
                        )}
                        {registro.lectura_anterior != null && registro.lectura_actual != null && (
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Lecturas</p>
                                <p className="text-sm font-medium text-gray-800">
                                    {registro.lectura_anterior} → {registro.lectura_actual}
                                </p>
                            </div>
                        )}
                        {registro.consumo_cantidad && (
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Consumo</p>
                                <p className="text-sm font-medium text-gray-800">
                                    {registro.consumo_cantidad.toLocaleString('es-PE')} {registro.unidad}
                                </p>
                            </div>
                        )}
                        {registro.monto_total && (
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Total a Pagar</p>
                                <p className="text-sm font-bold text-gray-900">
                                    S/. {Number(registro.monto_total).toFixed(2)}
                                </p>
                            </div>
                        )}
                        {registro.subido_por && (
                            <div>
                                <p className="text-xs text-gray-500 uppercase tracking-wide">Subido por</p>
                                <p className="text-sm text-gray-600">{registro.subido_por}</p>
                            </div>
                        )}
                    </div>

                    {/* Archivo original */}
                    {registro.archivo_nombre && (
                        <div className="flex items-center justify-between bg-white border border-gray-200 rounded-lg px-4 py-3">
                            <div className="flex items-center space-x-3">
                                <FileText className="h-5 w-5 text-red-500" />
                                <div>
                                    <p className="text-sm font-medium text-gray-800">{registro.archivo_nombre}</p>
                                    <p className="text-xs text-gray-400">Archivo original del recibo</p>
                                </div>
                            </div>
                            <button
                                onClick={() => onDescargar(registro.id, registro.archivo_nombre)}
                                className="flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                <Download className="h-4 w-4 mr-1" />
                                Descargar
                            </button>
                        </div>
                    )}

                    {/* Acciones */}
                    <div className="flex justify-end mt-3">
                        <button
                            onClick={() => onEliminar(registro.id)}
                            className="flex items-center px-3 py-1.5 text-red-600 border border-red-200 rounded-lg hover:bg-red-50 text-sm transition-colors"
                        >
                            <Trash2 className="h-4 w-4 mr-1" />
                            Eliminar
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

// ─── Modal de Reporte ─────────────────────────────────────────────────────────

const ModalReporte = ({ tipo, onCerrar }) => {
    const [reporte, setReporte] = useState(null);
    const [meses, setMeses] = useState(3);
    const [cargando, setCargando] = useState(false);

    const cargarReporte = async (m) => {
        setCargando(true);
        try {
            const data = await consumoService.reporte(tipo, m);
            setReporte(data);
        } catch {
            toast.error('Error al generar reporte');
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => { cargarReporte(meses); }, []);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b">
                    <div className="flex items-center space-x-2">
                        <BarChart3 className="h-6 w-6 text-blue-600" />
                        <h2 className="text-xl font-bold text-gray-900">
                            Reporte de Consumo — {tipo.charAt(0).toUpperCase() + tipo.slice(1)}
                        </h2>
                    </div>
                    <button onClick={onCerrar} className="text-gray-400 hover:text-gray-600">
                        <X className="h-6 w-6" />
                    </button>
                </div>

                <div className="p-6">
                    {/* Selector de meses */}
                    <div className="flex space-x-3 mb-6">
                        {[1, 3, 6].map((m) => (
                            <button
                                key={m}
                                onClick={() => { setMeses(m); cargarReporte(m); }}
                                className={`px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                                    meses === m
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                                }`}
                            >
                                {m} {m === 1 ? 'mes' : 'meses'}
                            </button>
                        ))}
                    </div>

                    {cargando && (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    )}

                    {reporte && !cargando && (
                        <>
                            {/* Resumen */}
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                                <div className="bg-blue-50 rounded-lg p-4 text-center">
                                    <p className="text-xs text-gray-500 mb-1">Total Consumo</p>
                                    <p className="text-xl font-bold text-blue-700">
                                        {reporte.total_consumo?.toLocaleString('es-PE')}
                                    </p>
                                    <p className="text-xs text-gray-400">{reporte.unidad}</p>
                                </div>
                                <div className="bg-green-50 rounded-lg p-4 text-center">
                                    <p className="text-xs text-gray-500 mb-1">Total Pagado</p>
                                    <p className="text-xl font-bold text-green-700">
                                        S/. {reporte.total_monto_soles?.toFixed(2)}
                                    </p>
                                </div>
                                <div className="bg-purple-50 rounded-lg p-4 text-center">
                                    <p className="text-xs text-gray-500 mb-1">Promedio Consumo</p>
                                    <p className="text-xl font-bold text-purple-700">
                                        {reporte.promedio_consumo?.toLocaleString('es-PE')}
                                    </p>
                                    <p className="text-xs text-gray-400">{reporte.unidad}/mes</p>
                                </div>
                                <div className="bg-orange-50 rounded-lg p-4 text-center">
                                    <p className="text-xs text-gray-500 mb-1">Promedio Pago</p>
                                    <p className="text-xl font-bold text-orange-700">
                                        S/. {reporte.promedio_monto_soles?.toFixed(2)}
                                    </p>
                                    <p className="text-xs text-gray-400">/mes</p>
                                </div>
                            </div>

                            {/* Variaciones mensuales */}
                            {reporte.variaciones_mensuales?.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Variación Mensual</h3>
                                    <div className="space-y-2">
                                        {reporte.variaciones_mensuales.map((v, i) => (
                                            <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                                                <span className="text-sm font-medium text-gray-700">{v.periodo}</span>
                                                <div className="flex items-center space-x-3">
                                                    <span className="text-sm text-gray-600">
                                                        {v.consumo?.toLocaleString('es-PE')} {reporte.unidad}
                                                    </span>
                                                    <span className={`flex items-center text-sm font-semibold ${
                                                        v.variacion_pct > 0 ? 'text-red-500' :
                                                        v.variacion_pct < 0 ? 'text-green-500' : 'text-gray-400'
                                                    }`}>
                                                        {v.variacion_pct > 0 ? <TrendingUp className="h-4 w-4 mr-1" /> :
                                                         v.variacion_pct < 0 ? <TrendingDown className="h-4 w-4 mr-1" /> :
                                                         <Minus className="h-4 w-4 mr-1" />}
                                                        {v.variacion_pct > 0 ? '+' : ''}{v.variacion_pct}%
                                                    </span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Detalle por mes con el origen de los registros */}
                            {reporte.total_registros > 0 && reporte.por_mes?.length > 0 && (
                                <div className="mt-6">
                                    <h3 className="text-sm font-semibold text-gray-700 mb-3">Detalle por mes</h3>
                                    <div className="space-y-2">
                                        {reporte.por_mes.map((m) => {
                                            const fuentes = [
                                                m.recibos ? `${m.recibos} recibo${m.recibos > 1 ? 's' : ''}` : null,
                                                m.manuales ? `${m.manuales} registro${m.manuales > 1 ? 's' : ''} manual${m.manuales > 1 ? 'es' : ''}` : null,
                                                m.fotos ? `${m.fotos} foto${m.fotos > 1 ? 's' : ''} (IA)` : null,
                                            ].filter(Boolean);
                                            return (
                                                <div key={m.periodo} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-700">{m.periodo}</span>
                                                        <p className="text-xs text-gray-400">{fuentes.length ? fuentes.join(' · ') : 'Sin registros'}</p>
                                                    </div>
                                                    <div className="text-right">
                                                        <span className="text-sm text-gray-700 font-semibold">
                                                            {m.consumo?.toLocaleString('es-PE')} {reporte.unidad}
                                                        </span>
                                                        {m.monto_soles > 0 && (
                                                            <p className="text-xs text-gray-400">S/. {m.monto_soles.toFixed(2)}</p>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {reporte.total_registros === 0 && (
                                <p className="text-center text-gray-400 py-6">
                                    No hay registros para el período seleccionado.
                                </p>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

// ─── Modal Registrar Residuo con Foto (IA de visión) ──────────────────────────

const TIPO_RESIDUO_LABEL = {
    organico: { label: 'Orgánico', color: 'text-yellow-700 bg-yellow-100' },
    reciclable: { label: 'Reciclable', color: 'text-green-700 bg-green-100' },
    no_reciclable: { label: 'No reciclable', color: 'text-gray-700 bg-gray-100' },
};

const ModalFotoResiduo = ({ onCerrar, onSubido }) => {
    const [almacen, setAlmacen] = useState('');
    const [archivo, setArchivo] = useState(null);
    const [subiendo, setSubiendo] = useState(false);
    const [resultado, setResultado] = useState(null);

    const handleSubir = async () => {
        if (!almacen) { toast.error('Selecciona un almacén'); return; }
        if (!archivo) { toast.error('Toma o selecciona una foto'); return; }
        setSubiendo(true);
        try {
            const data = await consumoService.subirFotoResiduo(almacen, archivo);
            setResultado(data);
            toast.success('Foto analizada y registrada');
            onSubido();
        } catch (error) {
            toast.error(error?.response?.data?.detail || 'Error al analizar la foto');
        } finally {
            setSubiendo(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
                <div className="flex items-center justify-between px-6 py-5 border-b">
                    <h2 className="text-lg font-bold text-gray-900">Registrar Residuo con Foto</h2>
                    <button onClick={onCerrar} className="text-gray-400 hover:text-gray-600"><X className="h-5 w-5" /></button>
                </div>
                <div className="px-6 py-5 space-y-4">
                    <p className="text-xs text-gray-500">
                        La IA estima el peso (kg) y el tipo de residuo a partir de la foto, y lo registra
                        automáticamente en el consumo del día — no requiere confirmación.
                    </p>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Almacén</label>
                        <select
                            value={almacen}
                            onChange={(e) => setAlmacen(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                        >
                            <option value="">— Selecciona —</option>
                            {ALMACENES.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Foto</label>
                        <input
                            type="file"
                            accept="image/*"
                            capture="environment"
                            onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                            className="w-full text-sm"
                        />
                    </div>

                    {resultado && (
                        <div className="border border-green-200 bg-green-50 rounded-lg p-3 text-sm space-y-1">
                            <p><span className="font-semibold">Peso estimado:</span> {resultado.peso_kg} kg</p>
                            <p>
                                <span className="font-semibold">Tipo:</span>{' '}
                                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${TIPO_RESIDUO_LABEL[resultado.tipo_residuo]?.color || ''}`}>
                                    {TIPO_RESIDUO_LABEL[resultado.tipo_residuo]?.label || resultado.tipo_residuo}
                                </span>
                            </p>
                            {resultado.descripcion_ia && <p className="text-gray-500 italic">"{resultado.descripcion_ia}"</p>}
                        </div>
                    )}
                </div>
                <div className="flex justify-end gap-3 px-6 pb-6">
                    <button onClick={onCerrar} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
                        {resultado ? 'Cerrar' : 'Cancelar'}
                    </button>
                    {!resultado && (
                        <button
                            onClick={handleSubir}
                            disabled={subiendo}
                            className="px-5 py-2 bg-green-600 text-white rounded-lg text-sm font-semibold disabled:opacity-60 flex items-center"
                        >
                            {subiendo && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>}
                            Analizar y registrar
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

const HistorialFotosResiduo = ({ fotos }) => {
    if (fotos.length === 0) return null;
    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mt-4">
            <h2 className="text-base font-semibold text-gray-700 mb-4">Historial de fotos de residuos</h2>
            <div className="space-y-2">
                {fotos.map((f) => (
                    <div key={f.id} className="flex items-center gap-3 border-b border-gray-100 py-2 last:border-0">
                        <a href={f.foto_url} target="_blank" rel="noreferrer">
                            <img src={f.foto_url} alt="residuo" className="w-12 h-12 object-cover rounded-lg border border-gray-200" />
                        </a>
                        <div className="flex-1 text-sm">
                            <p className="font-medium text-gray-800">{f.almacen} — {f.fecha}</p>
                            <p className="text-xs text-gray-500">{f.peso_kg} kg · {TIPO_RESIDUO_LABEL[f.tipo_residuo]?.label || f.tipo_residuo}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── Página Principal ─────────────────────────────────────────────────────────

const ConsumoServicioPage = () => {
    const [tabActiva, setTabActiva] = useState('energia');
    const [registros, setRegistros] = useState([]);
    const [cargando, setCargando] = useState(false);
    const [mostrarReporte, setMostrarReporte] = useState(false);
    const [mostrarModalKWH, setMostrarModalKWH] = useState(false);
    const [mostrarModalPDF, setMostrarModalPDF] = useState(false);
    const [mostrarModalFotoResiduo, setMostrarModalFotoResiduo] = useState(false);
    const [registrosKWH, setRegistrosKWH] = useState([]);
    const [fotosResiduo, setFotosResiduo] = useState([]);

    const cargarFotosResiduo = async () => {
        try {
            setFotosResiduo(await consumoService.listarFotosResiduo());
        } catch {
            setFotosResiduo([]);
        }
    };

    const cargarKWH = async (tipo) => {
        setRegistrosKWH([]);
        try {
            const data = await consumoService.listarKWH(null, tipo || tabActiva);
            setRegistrosKWH(data);
        } catch {
            toast.error(`Error al cargar los registros de ${tipo || tabActiva}`);
        }
    };

    const handleEliminarKWH = async (id) => {
        if (!window.confirm('¿Eliminar este registro de kWh?')) return;
        try {
            await consumoService.eliminarKWH(id);
            toast.success('Eliminado');
            setRegistrosKWH((prev) => prev.filter((r) => r.id !== id));
        } catch { toast.error('Error al eliminar'); }
    };

    const tabConfig = TABS.find((t) => t.key === tabActiva);

    const cargarRegistros = async (tipo) => {
        setCargando(true);
        setRegistros([]);
        try {
            const data = await consumoService.listar(tipo, 6);
            // Calcular variaciones para mostrar en accordions
            const conVariacion = data.map((r, i, arr) => {
                if (i < arr.length - 1) {
                    const anterior = arr[i + 1].consumo_cantidad || 0;
                    const actual = r.consumo_cantidad || 0;
                    if (anterior > 0) {
                        r._variacion_pct = Math.round(((actual - anterior) / anterior) * 100 * 10) / 10;
                    }
                }
                return r;
            });
            setRegistros(conVariacion);
        } catch (err) {
            toast.error('Error al cargar registros de consumo');
        } finally {
            setCargando(false);
        }
    };

    useEffect(() => {
        cargarRegistros(tabActiva);
        cargarKWH(tabActiva);
        if (tabActiva === 'residuos') cargarFotosResiduo();
    }, [tabActiva]);


    const handleEliminar = async (id) => {
        if (!window.confirm('¿Eliminar este registro y su PDF?')) return;
        try {
            await consumoService.eliminar(id);
            toast.success('Registro eliminado');
            setRegistros((prev) => prev.filter((r) => r.id !== id));
        } catch {
            toast.error('Error al eliminar');
        }
    };

    const handleDescargar = async (id, nombre) => {
        try {
            await consumoService.descargarPDF(id, nombre);
        } catch {
            toast.error('Error al descargar el PDF');
        }
    };

    return (
        <Layout>
            <div className="p-6">
                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-2xl font-bold text-gray-900">Consumo de Servicios</h1>
                    <p className="text-gray-500 text-sm mt-1">
                        Gestiona y monitorea el consumo de energía, agua y otros servicios.
                    </p>
                </div>

                {/* Tabs */}
                <div className="flex flex-wrap gap-2 mb-6">
                    {TABS.map((tab) => {
                        const Icon = tab.icon;
                        const colors = TAB_COLORS[tab.color];
                        return (
                            <button
                                key={tab.key}
                                onClick={() => setTabActiva(tab.key)}
                                className={`flex items-center px-5 py-2.5 rounded-lg border font-medium text-sm transition-colors ${
                                    tabActiva === tab.key ? colors.active : colors.inactive
                                }`}
                            >
                                <Icon className="h-4 w-4 mr-2" />
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                {/* Zona de carga y botón reporte */}
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
                    {/* Upload recibo (PDF/Excel/CSV) */}
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={() => setMostrarModalPDF(true)}
                            className="flex items-center px-4 py-2.5 rounded-lg border-2 border-dashed bg-white text-blue-600 border-blue-300 hover:bg-blue-50 hover:border-blue-500 transition-colors font-medium text-sm"
                        >
                            <Upload className="h-4 w-4 mr-2" />
                            Subir recibo ({tabConfig?.label}) · PDF/Excel/CSV
                        </button>
                    </div>

                    {/* Botones derecha */}
                    <div className="flex items-center space-x-3">
                        {CONSUMO_CONFIG[tabActiva] && tabActiva !== 'otro' && (
                            <button
                                onClick={() => setMostrarModalKWH(true)}
                                className={`flex items-center px-4 py-2.5 ${MODAL_COLORS[CONSUMO_CONFIG[tabActiva].color].btn} text-white rounded-lg transition-colors text-sm font-semibold`}
                            >
                                <Plus className="h-4 w-4 mr-1.5" />
                                Agregar {CONSUMO_CONFIG[tabActiva].unidad}
                            </button>
                        )}
                        {tabActiva === 'residuos' && (
                            <button
                                onClick={() => setMostrarModalFotoResiduo(true)}
                                className="flex items-center px-4 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm font-semibold"
                            >
                                📷 Registrar con foto (IA)
                            </button>
                        )}
                        <button
                            onClick={() => setMostrarReporte(true)}
                            className="flex items-center px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                        >
                            <BarChart3 className="h-4 w-4 mr-2" />
                            Ver Reporte
                        </button>
                    </div>
                </div>

                {/* Lista de consumos */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
                    <div className="flex items-center mb-4">
                        {React.createElement(tabConfig.icon, {
                            className: `h-5 w-5 mr-2 ${TAB_COLORS[tabConfig.color].icon}`,
                        })}
                        <h2 className="text-lg font-semibold text-gray-800">
                            Consumo de {tabConfig?.label} — últimos meses
                        </h2>
                    </div>

                    {cargando && (
                        <div className="flex justify-center py-10">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    )}

                    {!cargando && registros.length === 0 && (
                        <div className="text-center py-12 text-gray-400">
                            <FileText className="h-12 w-12 mx-auto mb-3 opacity-30" />
                            <p className="text-sm">No hay registros de {tabConfig?.label.toLowerCase()} aún.</p>
                            <p className="text-xs mt-1">Sube un recibo (PDF, Excel o CSV) para comenzar.</p>
                        </div>
                    )}

                    {!cargando && registros.length > 0 && (
                        <div>
                            {registros.map((registro) => (
                                <ConsumoAccordion
                                    key={registro.id}
                                    registro={registro}
                                    onEliminar={handleEliminar}
                                    onDescargar={handleDescargar}
                                    tabColor={tabConfig.color}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* Lista de consumo por almacén (energía/agua/gas) */}
                {tabActiva !== 'otro' && (
                    <ListaKWH registros={registrosKWH} onEliminar={handleEliminarKWH} tipo={tabActiva} />
                )}

                {/* Historial de fotos de residuos (registradas por IA) */}
                {tabActiva === 'residuos' && <HistorialFotosResiduo fotos={fotosResiduo} />}
            </div>

            {/* Modal Registrar Residuo con Foto */}
            {mostrarModalFotoResiduo && (
                <ModalFotoResiduo
                    onCerrar={() => setMostrarModalFotoResiduo(false)}
                    onSubido={cargarFotosResiduo}
                />
            )}

            {/* Modal Subir PDF */}
            {mostrarModalPDF && (
                <ModalSubirPDF
                    tipo={tabActiva}
                    onCerrar={() => setMostrarModalPDF(false)}
                    onSubido={() => cargarRegistros(tabActiva)}
                />
            )}

            {/* Modal de consumo manual por almacén */}
            {mostrarModalKWH && (
                <ModalKWH
                    tipo={tabActiva}
                    onCerrar={() => setMostrarModalKWH(false)}
                    onGuardado={() => cargarKWH(tabActiva)}
                />
            )}

            {/* Modal de Reporte */}
            {mostrarReporte && (
                <ModalReporte tipo={tabActiva} onCerrar={() => setMostrarReporte(false)} />
            )}
        </Layout>
    );
};

export default ConsumoServicioPage;
